import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { positionDisplayState } from '@/lib/compliance-position'
import type { PositionValue, UnassessedReason } from '@/types/extraction'

interface PositionOption {
  value: PositionValue
  label: string
  description: string
  variant: BadgeProps['variant']
  dotColor: string
}

/** The five selectable enum values, in the order a reviewer scans them. */
export const POSITION_OPTIONS: PositionOption[] = [
  {
    value: 'comply',
    label: 'Comply',
    description: 'Evidence shows we meet it',
    variant: 'success',
    dotColor: 'bg-[hsl(var(--status-success))]',
  },
  {
    value: 'partial',
    label: 'Partial',
    description: 'Met with a departure or shortfall',
    variant: 'warning',
    dotColor: 'bg-[hsl(var(--status-warning))]',
  },
  {
    value: 'non_comply',
    label: 'Non-comply',
    description: 'Evidence shows a gap',
    variant: 'destructive',
    dotColor: 'bg-destructive',
  },
  {
    value: 'not_applicable',
    label: 'N/A',
    description: "Doesn't apply to us",
    variant: 'secondary',
    dotColor: 'bg-muted-foreground',
  },
  {
    value: 'unassessed',
    // Selecting this WITHDRAWS the position: the server clears the rationale,
    // confidence, citations and assessed stamp, and hands the row back to the
    // assessor (`positionSource` → 'ai'). The copy has to say that, because it
    // is also the only way out of a manual position.
    label: 'Unassessed',
    description: 'Clear the verdict — AI can assess it again',
    variant: 'outline',
    dotColor: 'bg-muted-foreground',
  },
]

const OPTION_BY_VALUE = new Map(POSITION_OPTIONS.map((o) => [o.value, o]))

/** Why the pill reads "Needs your confirmation" — the only pill with a tooltip. */
const NEEDS_CONFIRMATION_TOOLTIP =
  "The AI can't check this against your library — confirm it yourself."

/**
 * The label the user reads. Splits `unassessed` by whether an assessment has
 * ever run against this requirement and, if it has, by whether the assessor
 * searched the library for it at all (mig 180) — the same split as
 * `positionDisplayState`. Every surface that prints a position (the pill, the
 * matrix filters and groups, the export, the drawer) goes through this, so a
 * row reads the same everywhere.
 */
export function displayPositionLabel(
  position: PositionValue,
  assessedAt: string | null,
  unassessedReason?: UnassessedReason | null
): string {
  switch (positionDisplayState(position, assessedAt, unassessedReason)) {
    case 'not_assessed':
      return 'Not assessed'
    case 'needs_review':
      return 'Needs review'
    case 'needs_confirmation':
      return 'Needs your confirmation'
    default:
      return OPTION_BY_VALUE.get(position)?.label ?? 'Not assessed'
  }
}

export interface PositionPillProps {
  position: PositionValue
  positionSource: 'ai' | 'manual'
  assessedAt: string | null
  /** Why the AI left it unassessed (mig 180). Changes the label only. */
  unassessedReason?: UnassessedReason | null | undefined
  disabled?: boolean | undefined
  /** Value override — the caller PATCHes and stamps the source as 'manual'. */
  onChange: (next: PositionValue) => void
}

export function PositionPill({
  position,
  positionSource,
  assessedAt,
  unassessedReason,
  disabled,
  onChange,
}: PositionPillProps) {
  const option = OPTION_BY_VALUE.get(position) ?? POSITION_OPTIONS[POSITION_OPTIONS.length - 1]!
  const label = displayPositionLabel(position, assessedAt, unassessedReason)
  const state = positionDisplayState(position, assessedAt, unassessedReason)
  const isUnassessed = position === 'unassessed'
  // The stamp, not the source, is what makes this AI-set. `positionSource` is a
  // write-time ownership guard (`NOT NULL DEFAULT 'ai'`), so every row the
  // assessor has never touched already reads 'ai' — on its own it would put a ✦
  // and a "set by AI" announcement on every row of an unassessed bid.
  const isAiSet = positionSource === 'ai' && assessedAt != null

  const trigger = (
    <SelectTrigger
      aria-label={`Position: ${label}${isAiSet ? ' (set by AI)' : ''}`}
      className="h-7 w-auto text-xs border-0 bg-transparent hover:bg-accent/50 transition-colors gap-1.5 px-2"
    >
      <SelectValue>
        <Badge
          data-testid="position-pill-badge"
          data-display-state={state}
          data-dashed={isUnassessed ? 'true' : 'false'}
          variant={option.variant}
          dot
          className={cn(
            'text-xs font-normal whitespace-nowrap',
            isUnassessed && 'border border-dashed'
          )}
        >
          {label}
          {isAiSet && (
            <span aria-hidden="true" className="text-[0.9em] opacity-70">
              ✦
            </span>
          )}
        </Badge>
      </SelectValue>
    </SelectTrigger>
  )

  return (
    <Select
      value={position}
      onValueChange={(next) => onChange(next as PositionValue)}
      disabled={disabled ?? false}
    >
      {/* On the trigger, not the badge: Radix renders the select value with
          pointer events off, so the badge itself is never hovered. */}
      {state === 'needs_confirmation' ? (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            <TooltipContent>{NEEDS_CONFIRMATION_TOOLTIP}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        trigger
      )}
      <SelectContent>
        {POSITION_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            <span className="flex items-start gap-2 text-xs">
              <span className={cn('mt-1 h-2 w-2 shrink-0 rounded-full', o.dotColor)} />
              <span className="flex flex-col">
                <span>{o.label}</span>
                <span className="text-[11px] text-muted-foreground">{o.description}</span>
              </span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
